/**
 * SpamSettingsDialog — AI spam filter + blacklist management + auto-cleanup.
 *
 * Per mailbox (chosen in the header): the (paid) AI opt-in, its rules, and the
 * auto-cleanup sweep that moves aged Spam into Trash. Across all mailboxes: the
 * trust / blocking preferences and the allow / block lists (SenderListsPanel —
 * searchable, paged, tagged by mailbox). Nothing here ever permanently deletes
 * mail — Trash is only ever emptied manually.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { useSpamSettings, useSetSpamSettings, useSpamSpend } from '../hooks/useSpam';
import { useEmailUserSettings, useSetEmailUserSettings } from '../hooks/useEmailUserSettings';
import { SenderListsPanel } from './SenderListsPanel';
import { useDialogAccount } from '../hooks/useDialogAccount';
import { DialogAccountPicker, ALL_MAILBOXES } from './DialogAccountPicker';

interface SpamSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seed account; the dialog has its own in-popup picker to switch. */
  defaultAccountId?: string;
}

/** Credits == cents on the platform (1 credit = $0.01). */
function formatCredits(cents: number): string {
  const credits = cents.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${credits} credit${cents === 1 ? '' : 's'}`;
}

/**
 * Holder-wide trust + blocking preferences (all mailboxes): whether Contact Book
 * contacts count as trusted senders, and whether reporting spam blocks the
 * sender in every mailbox.
 */
function TrustAndBlockingSection({ multipleMailboxes }: { multipleMailboxes: boolean }) {
  const settings = useEmailUserSettings();
  const setAll = useSetEmailUserSettings();
  return (
    <section className="space-y-2 border-t pt-3">
      <h3 className="text-sm font-semibold">Trust &amp; blocking · all mailboxes</h3>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={settings.trustContacts}
          disabled={setAll.isPending}
          onChange={(e) => setAll.mutate({ ...settings, trustContacts: e.target.checked })}
        />
        <span>
          Trust my contacts
          <span className="block text-xs text-muted-foreground">
            Mail from people in your Contact Book stays in your Inbox, like mail from people you’ve
            emailed — unless you block them, or it fails sender authentication.
          </span>
        </span>
      </label>
      {multipleMailboxes && (
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={settings.blockAcrossMailboxes}
            disabled={setAll.isPending}
            onChange={(e) => setAll.mutate({ ...settings, blockAcrossMailboxes: e.target.checked })}
          />
          <span>
            When I report spam, block the sender in all my mailboxes
            <span className="block text-xs text-muted-foreground">
              Off: a sender is blocked only in the mailbox the spam arrived in.
            </span>
          </span>
        </label>
      )}
    </section>
  );
}

export function SpamSettingsDialog({
  open,
  onOpenChange,
  defaultAccountId,
}: SpamSettingsDialogProps) {
  const { accounts, selectedAccountId, setSelectedAccountId } = useDialogAccount(defaultAccountId);
  // ONE scope for the whole dialog: a mailbox, or "All mailboxes". Opened from
  // the All-accounts view (no defaultAccountId) it starts on All mailboxes
  // rather than silently picking the primary; re-evaluated on every open.
  const multi = accounts.length > 1;
  const [allScope, setAllScope] = useState(!defaultAccountId);
  // The dialog stays mounted between opens, so reset BOTH halves of the scope on
  // each open — it always starts on the mailbox being viewed, not the last pick.
  useEffect(() => {
    if (!open) return;
    setAllScope(!defaultAccountId);
    if (defaultAccountId) setSelectedAccountId(defaultAccountId);
  }, [open, defaultAccountId, setSelectedAccountId]);
  const scopeAll = multi && allScope;
  const onScopeChange = (value: string) => {
    if (value === ALL_MAILBOXES) {
      setAllScope(true);
    } else {
      setAllScope(false);
      setSelectedAccountId(value);
    }
  };
  const { data: settings, isLoading } = useSpamSettings(selectedAccountId, open && !scopeAll);
  // Spend is billed per user (across every account), so it's fetched once.
  const { data: spend } = useSpamSpend(open);
  const save = useSetSpamSettings(selectedAccountId ?? '');
  const [rules, setRules] = useState('');
  const [editingRules, setEditingRules] = useState(false);
  const [autoCleanup, setAutoCleanup] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);

  // Seed the editors from the saved settings (or defaults) once loaded.
  // Rules always seed back into read-only view — editing is gated behind Edit.
  useEffect(() => {
    if (settings) {
      setRules(settings.rules ?? settings.defaultRules);
      setEditingRules(false);
      setAutoCleanup(settings.spamRetention.spamToTrash.enabled);
      setCleanupDays(settings.spamRetention.spamToTrash.days);
    }
  }, [settings]);

  const enabled = settings?.enabled ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Spam &amp; AI Filter</DialogTitle>
          {/* The one scope for everything below: the AI filter settings (a single
              mailbox) and the sender lists (a mailbox, or all of them). */}
          <DialogAccountPicker
            accounts={accounts}
            value={scopeAll ? ALL_MAILBOXES : selectedAccountId}
            onChange={onScopeChange}
            allOption
          />
        </DialogHeader>
        <DialogBody>
          {!selectedAccountId ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Connect an account to use AI spam filtering.
            </p>
          ) : (
            <div className="space-y-4">
              {scopeAll ? (
                <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  The AI spam filter is set per mailbox — pick a mailbox above to change it.
                </p>
              ) : isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={enabled}
                      disabled={save.isPending}
                      onChange={(e) => save.mutate({ enabled: e.target.checked })}
                    />
                    <span>
                      Enable AI spam filtering
                      <span className="block text-xs text-muted-foreground">
                        Mail from senders you haven’t emailed before is reviewed by AI and moved to
                        Spam if it fails your rules. This uses credits per reviewed sender. Senders
                        you’ve emailed or approved are always allowed.
                      </span>
                    </span>
                  </label>

                  {/* What the AI spam filter has cost (user-wide, across accounts). */}
                  {spend && (
                    <div className="rounded-md border bg-muted/30 p-2 text-xs">
                      <p className="mb-1 font-medium text-foreground">AI spam filter spend</p>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                        <dt>Today</dt>
                        <dd className="text-right tabular-nums text-foreground">
                          {formatCredits(spend.dayCents)}
                        </dd>
                        <dt>Last 7 days</dt>
                        <dd className="text-right tabular-nums text-foreground">
                          {formatCredits(spend.weekCents)}
                        </dd>
                        <dt>Last 30 days</dt>
                        <dd className="text-right tabular-nums text-foreground">
                          {formatCredits(spend.monthCents)}
                        </dd>
                        <dt>All time</dt>
                        <dd className="text-right tabular-nums text-foreground">
                          {formatCredits(spend.allCents)}
                        </dd>
                      </dl>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        Credits spent letting the AI review first-time senders (1 credit = $0.01).
                      </p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Spam rules</p>
                      {!editingRules && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={save.isPending}
                          onClick={() => setEditingRules(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Plain-language rules the AI uses to decide what counts as spam.
                      {editingRules ? ' Edit to fit your needs.' : ' Click Edit to change them.'}
                    </p>
                    <Textarea
                      rows={8}
                      value={rules}
                      readOnly={!editingRules}
                      disabled={save.isPending}
                      onChange={(e) => setRules(e.target.value)}
                      aria-label="Spam rules"
                      className={
                        editingRules
                          ? 'text-sm'
                          : 'cursor-default bg-muted/40 text-sm text-muted-foreground'
                      }
                    />
                    {editingRules && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={save.isPending}
                          onClick={() => {
                            if (settings) setRules(settings.rules ?? settings.defaultRules);
                            setEditingRules(false);
                          }}
                        >
                          Cancel
                        </Button>
                        {settings && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={save.isPending}
                            onClick={() => setRules(settings.defaultRules)}
                          >
                            Reset to default
                          </Button>
                        )}
                        <Button
                          size="sm"
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({ rules }, { onSuccess: () => setEditingRules(false) })
                          }
                        >
                          {save.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Save rules'
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium">Auto-cleanup</p>
                    <p className="text-xs text-muted-foreground">
                      Automatically move mail out of Spam into Trash after a set time. Trash is
                      never emptied automatically — you always delete permanently yourself.
                    </p>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={autoCleanup}
                        disabled={save.isPending}
                        onChange={(e) => setAutoCleanup(e.target.checked)}
                      />
                      <span className="flex items-center gap-1">
                        Move Spam to Trash after
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={cleanupDays}
                          disabled={!autoCleanup || save.isPending}
                          onChange={(e) =>
                            setCleanupDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))
                          }
                          className="w-16 rounded border px-1 py-0.5 text-sm"
                          aria-label="Days before moving Spam to Trash"
                        />
                        days
                      </span>
                    </label>
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        disabled={save.isPending}
                        onClick={() =>
                          save.mutate({
                            spamRetention: {
                              spamToTrash: { enabled: autoCleanup, days: cleanupDays },
                            },
                          })
                        }
                      >
                        {save.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          'Save auto-cleanup'
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
              <TrustAndBlockingSection multipleMailboxes={multi} />
              <SenderListsPanel
                accounts={accounts}
                accountId={scopeAll ? undefined : selectedAccountId}
              />
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
